// Stats-scope toggle: which reports feed a statistic (see
// docs/document-tags-scoped-stats.md). Mirrors TimeRangeControl and
// reads/writes the `?scope=` URL param so reloads preserve state and the
// value is sharable.
import { useSearchParams } from 'react-router-dom';
import type { StatsScope } from '../types';

const OPTIONS: { value: StatsScope; label: string; hint: string }[] = [
  { value: 'all', label: 'All reports', hint: 'Every report that mentions the IOC.' },
  {
    value: 'attributed',
    label: 'Attributed',
    hint: 'Reports linked to a malware family or actor; duplicates and dummy pages excluded.',
  },
  {
    value: 'focused',
    label: 'Single-family',
    hint: 'Attributed reports about exactly one Malpedia family — the high-trust set.',
  },
];

export function useStatsScope(defaultScope: StatsScope = 'all'): StatsScope {
  const [params] = useSearchParams();
  const raw = (params.get('scope') || defaultScope) as StatsScope;
  return OPTIONS.some((o) => o.value === raw) ? raw : defaultScope;
}

export default function ScopeToggle({ defaultScope = 'all' }: { defaultScope?: StatsScope } = {}) {
  const [params, setParams] = useSearchParams();
  const current = useStatsScope(defaultScope);

  const setScope = (value: StatsScope) => {
    const next = new URLSearchParams(params);
    next.set('scope', value);
    setParams(next, { replace: true });
  };

  return (
    <div className="trends-range-control" role="tablist" aria-label="Report scope">
      {OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          role="tab"
          aria-selected={current === opt.value}
          title={opt.hint}
          className={`trends-range-btn${current === opt.value ? ' active' : ''}`}
          onClick={() => setScope(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
